/**
 * Platform-agnostic chart theme.
 *
 * Both webapp (MUI) and mobile (React Native Paper) map their
 * platform theme to this common interface before passing it to presets.
 */
export interface ChartTheme {
  colors: {
    primary: string;
    secondary: string;
    error: string;
    warning: string;
    info: string;
    success: string;
    /** Hot temperature color (used for forecast curves) */
    hot: string;
    /** Main text color */
    text: string;
    /** Secondary/muted text color */
    textSecondary: string;
    /** Disabled text color */
    textDisabled: string;
    /** Chart / tooltip background */
    background: string;
    /** Card / surface background */
    surface: string;
    /** Pre-computed grid line color (typically text with ~12% alpha) */
    grid: string;
    /** Crosshair line color */
    crosshair: string;
  };
  /** Whether the current theme is dark mode */
  isDark: boolean;
  /** Border radius in pixels */
  borderRadius: number;
}

/** Light-mode palette for multi-year comparison charts (10 distinct colors) */
const YEAR_COLORS_LIGHT = [
  '#1976D2', // blue
  '#388E3C', // green
  '#E65100', // orange
  '#C2185B', // pink
  '#7B1FA2', // purple
  '#00838F', // cyan
  '#5D4037', // brown
  '#455A64', // blue-gray
  '#D32F2F', // red
  '#303F9F', // indigo
] as const;

/** Dark-mode palette for multi-year comparison charts (10 distinct colors) */
const YEAR_COLORS_DARK = [
  '#64B5F6', // blue
  '#81C784', // green
  '#FFB74D', // orange
  '#F06292', // pink
  '#CE93D8', // purple
  '#4DD0E1', // cyan
  '#A1887F', // brown
  '#90A4AE', // blue-gray
  '#EF5350', // red
  '#7986CB', // indigo
] as const;

/**
 * @deprecated Use the isDark-aware overload instead.
 * Kept for backward-compat; returns the light palette.
 */
export const YEAR_COLORS = YEAR_COLORS_LIGHT;

/** Light-mode multi-series color palette */
const SERIES_PALETTE_LIGHT = [
  '#1565C0', // dark blue
  '#00897B', // teal
  '#6A1B9A', // purple
  '#C62828', // red
  '#2E7D32', // green
  '#D81B60', // pink
  '#E65100', // orange
  '#4527A0', // deep purple
  '#00838F', // dark cyan
  '#AD1457', // magenta
  '#33691E', // olive
  '#BF360C', // deep orange
  '#1B5E20', // dark green
] as const;

/** Dark-mode multi-series color palette */
const SERIES_PALETTE_DARK = [
  '#64B5F6', // blue
  '#4DB6AC', // teal
  '#CE93D8', // purple
  '#EF5350', // red
  '#81C784', // green
  '#F06292', // pink
  '#FFB74D', // orange
  '#B39DDB', // deep purple
  '#4DD0E1', // cyan
  '#F48FB1', // magenta
  '#AED581', // olive
  '#FF8A65', // deep orange
  '#A5D6A7', // light green
] as const;

/**
 * @deprecated Use the isDark-aware overload instead.
 * Kept for backward-compat; returns the light palette.
 */
export const SERIES_PALETTE = SERIES_PALETTE_LIGHT;

/** Light-mode wind speed range colors (7 bins, low to high) */
const WIND_ROSE_COLORS_LIGHT = [
  '#80DEEA', // calm - light cyan
  '#69D06C', // light green
  '#8BC34A', // yellow-green
  '#F9C929', // yellow
  '#F9A825', // golden
  '#FB8C00', // orange
  '#E64A19', // strong - dark orange
] as const;

/** Dark-mode wind speed range colors (7 bins, low to high) */
const WIND_ROSE_COLORS_DARK = [
  '#80DEEA', // calm - cyan
  '#81C784', // light green
  '#AED581', // yellow-green
  '#FFF176', // yellow
  '#FFD54F', // golden
  '#FFB74D', // orange
  '#FF8A65', // strong - salmon
] as const;

/**
 * @deprecated Use getWindRoseColors(isDark) instead.
 * Kept for backward-compat; returns the light palette.
 */
export const WIND_ROSE_COLORS = WIND_ROSE_COLORS_LIGHT;

/**
 * Get the wind rose color palette appropriate for the current theme.
 */
export const getWindRoseColors = (isDark: boolean): readonly string[] =>
  isDark ? WIND_ROSE_COLORS_DARK : WIND_ROSE_COLORS_LIGHT;

/**
 * Pick a color from the SERIES_PALETTE, cycling if index exceeds length.
 * When isDark is provided, returns a theme-appropriate color.
 */
export const getSeriesColor = (index: number, isDark?: boolean): string => {
  const palette = isDark === true ? SERIES_PALETTE_DARK : SERIES_PALETTE_LIGHT;
  return palette[index % palette.length];
};

/**
 * Pick a year color from the YEAR_COLORS palette, cycling if needed.
 * When isDark is provided, returns a theme-appropriate color.
 */
export const getYearColor = (index: number, isDark?: boolean): string => {
  const palette = isDark === true ? YEAR_COLORS_DARK : YEAR_COLORS_LIGHT;
  return palette[index % palette.length];
};

/** Semantic color pairs for specific metric types (light + dark). */
export const METRIC_COLORS = {
  dewPoint: {light: '#0097A7', dark: '#4DD0E1'} as const,
  feelsLike: {light: '#E65100', dark: '#FFB74D'} as const,
  apparentTemperature: {light: '#7B1FA2', dark: '#CE93D8'} as const,
  humidity: {light: '#1565C0', dark: '#64B5F6'} as const,
} as const;

export type MetricColorKey = keyof typeof METRIC_COLORS;

/** Get a semantic metric color for the current theme. */
export const getMetricColor = (
  key: MetricColorKey,
  isDark: boolean,
): string => (isDark ? METRIC_COLORS[key].dark : METRIC_COLORS[key].light);
